import { GoogleStorageVideo } from './GoogleStorageVideo'
import { Spinner } from './Spinner'
import { Dialog, InlineDialog } from './dialogs/Dialog'
import { DialogCloseButton } from './dialogs/DialogCloseButton'
import { useTranslationTyped } from 'i18n/i18n'
import { useEffect, useState } from 'react'
import { useStorage } from 'hooks/auth'
import { resolveStorageUrl, videoVttSidecarUrl } from 'util/storageUrl'
import { useVttBlobUrl } from 'hooks/useVttBlobUrl'

/**
 * Resolves a storage URL to a directly-playable (token-authenticated) URL so a
 * plain <video> can stream it via range requests — the same way the meeting
 * page plays video. When disabled, returns the input unchanged. Returns
 * undefined while resolving.
 */
function useResolvedUrl(src: string | undefined, enabled: boolean) {
  const storage = useStorage()
  const [resolved, setResolved] = useState<string | undefined>(
    enabled ? undefined : src
  )

  useEffect(() => {
    if (!enabled) {
      setResolved(src)
      return
    }
    if (!src) {
      setResolved(undefined)
      return
    }
    let cancelled = false
    // Clear the previous URL so the <video> doesn't briefly point at the old
    // source while the new one resolves.
    setResolved(undefined)
    resolveStorageUrl(storage, src)
      .then((url) => {
        if (!cancelled) setResolved(url)
      })
      .catch(() => {
        // Fall back to the raw URL rather than showing nothing.
        if (!cancelled) setResolved(src)
      })
    return () => {
      cancelled = true
    }
  }, [storage, src, enabled])

  return resolved
}

export function VideoPreviewDialog({
  src,
  streamingSrc,
  vttUrl,
  inline = false,
  onDismiss,
  requiresAuthHeaders = false,
  skipCache = false,
  alt,
}: {
  src: string
  // Preferred playback source (the transcoded .streaming.mp4). Falls back to
  // `src` when absent. Captions are still derived from `src`, since the VTT
  // lives next to the original file, not the streaming file.
  streamingSrc?: string
  // Pre-signed VTT caption URL from the API (getSlideDeckForAssignment).
  // Preferred over deriving the caption URL from `src`.
  vttUrl?: string
  inline?: boolean
  onDismiss?: () => void
  requiresAuthHeaders?: boolean
  skipCache?: boolean
  alt: string
}) {
  const { tt } = useTranslationTyped()
  const DialogToUse = inline ? InlineDialog : Dialog
  const playbackSrc = streamingSrc || src
  // On the non-auth path, resolve to a token URL and stream it in a plain
  // <video> (like the meeting page) instead of blob-downloading the whole file.
  const resolvedPlaybackSrc = useResolvedUrl(playbackSrc, !requiresAuthHeaders)
  // Prefer the pre-signed vttUrl from the API. Otherwise derive the caption URL
  // by swapping .mp4/.webm with .vtt — but only when src is NOT signed. A signed
  // URL's signature is bound to the .mp4 path, so extension-swapping it produces
  // an invalid signature (SignatureDoesNotMatch). Derived (unsigned) paths get
  // re-resolved downstream by resolveStorageUrl.
  const vttSrc = vttUrl ?? videoVttSidecarUrl(src)

  // For the non-auth path, fetch the VTT and create a same-origin blob
  // URL so the native <track> element can load it without CORS issues.
  const vttBlobUrl = useVttBlobUrl(!requiresAuthHeaders ? vttSrc : undefined)

  return (
    <DialogToUse
      onDismiss={onDismiss}
      size="custom"
      className="relative flex aspect-video max-h-[90vh] w-[90vw] flex-col overflow-hidden rounded-2xl bg-surface p-0!"
      innerClassName="p-0! overflow-hidden flex flex-col items-center h-full "
      aria-labelledby={undefined}
      aria-label={tt.videos_preview.video_preview()}
    >
      <DialogCloseButton
        className="z-50"
        onClick={() => {
          if (onDismiss) onDismiss()
          // returning true pops dialog
          return !inline
        }}
      />
      <div className="aspect-video h-full w-full overflow-hidden">
        {/* The two <video> branches sit in the same position, so React keeps
            the same element (and playback) when the caption file loads. */}
        {!requiresAuthHeaders && vttBlobUrl ? (
          <video
            autoPlay
            className="aspect-video! max-h-full w-full max-w-full rounded-2xl"
            controls
            src={resolvedPlaybackSrc}
          >
            {/* captions, not subtitles: the VTT is a same-language speech
                transcript (golang/server/cmd/video_captions) and is the only text
                alternative for viewers who can't hear the audio. It has no sound-effect
                cues yet. */}
            <track
              kind="captions"
              srcLang="en"
              label={tt.videos_preview.english()}
              src={vttBlobUrl}
            />
          </video>
        ) : !requiresAuthHeaders ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption -- no caption file exists (or it has not loaded yet) for this video
          <video
            autoPlay
            className="aspect-video! max-h-full w-full max-w-full rounded-2xl"
            controls
            src={resolvedPlaybackSrc}
          />
        ) : (
          <GoogleStorageVideo
            namespace="preview-dialog"
            skipCache={skipCache}
            autoPlay
            className="aspect-video! max-h-full w-full max-w-full rounded-2xl"
            controls
            crossOrigin="anonymous"
            src={playbackSrc}
            vttSrc={vttSrc}
            vttLabel={tt.videos_preview.english()}
            alt={alt}
            loader={
              <div className="flex aspect-video min-w-[300px] items-center justify-center sm:min-w-[400px] md:min-w-[600px]">
                <div>
                  <div className="mb-2 text-label-medium">
                    {tt.videos_preview.loading_video()}
                  </div>
                  <div className="flex justify-center">
                    <Spinner />
                  </div>
                </div>
              </div>
            }
          />
        )}
      </div>
    </DialogToUse>
  )
}
